import { useRouteLoaderData, useSearchParams } from 'react-router';
import { data as routerData } from 'react-router';
import type { MetaDescriptor } from 'react-router';
import type { LoaderFunctionArgs } from '~/types/route-args';
import ArticlesPage from '@modules/professional/page/ArticlesPage';
import { loader as articlesLoader } from '@modules/professional/page/loaders/articles-page-loader.server';
import { loader as overviewLoader } from '@modules/professional/page/loaders/overview-page-loader.server';
import type { loader as layoutLoader } from '@modules/professional/page/loaders/professional-page-loader.server';
import { isProfessionalRevampActive } from '@modules/professional/util/is-professional-revamp-active.server';
import { ProfessionalArticlesPageRevamp } from '~/ui-rocco/pages/professional-detail/ProfessionalArticlesPageRevamp';
import { getPreloadImageLink } from '~/modules/images';
import { shouldRevalidateLeaf } from '~/modules/root/util/should-revalidate-leaf';
import type { APHandle } from '~/modules/root';
import type { SearchResultItem, SearchResults } from '~/modules/search';
import { mergeMeta } from '~/modules/seo';
import { getImagesSizes } from '~/utils/image-sizes-utils';

/**
 * When the professional revamp is active the Articles tab becomes a full
 * editorial page that reuses the overview sections, so the loader merges
 * overview-shaped data with the article ES `filterStates` that drive the tab's
 * search + sort bar. Otherwise it serves the legacy paginated journal data.
 */
export const loader = async (args: LoaderFunctionArgs) => {
    if (!isProfessionalRevampActive(args.context, args.request)) {
        return articlesLoader(args);
    }

    const [overviewResponse, articlesData] = await Promise.all([
        overviewLoader(args),
        articlesLoader(args),
    ]);

    return routerData(
        {
            ...overviewResponse.data,
            filterStates: articlesData.filterStates,
            // Expose the article ES hits so `meta` can preload the first tiles'
            // images (same q/sort as the rendered grid). Without this the merged
            // overview data has no `searchResult` and the preload is skipped.
            searchResult: articlesData.searchResult,
        },
        { headers: overviewResponse.init?.headers ?? undefined },
    );
};

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data } = props;
        const isDesktop = data?.deviceType === 'desktop';
        const hits =
            data && 'searchResult' in data
                ? data.searchResult?.hits
                : undefined;
        const preloadImages = hits
            ? prefetchProfessionalJournalImages(hits, isDesktop)
            : [];
        return [...preloadImages];
    },
);

export const prefetchProfessionalJournalImages = (
    hits: SearchResults<SearchResultItem>['hits'],
    isDesktop: boolean,
) => {
    const numberTilesToPreload = isDesktop ? 4 : 2;

    return hits.slice(0, numberTilesToPreload).reduce((acc, hit, index) => {
        if (!hit.LibraryThumbs?.Thumb_lg) return acc;
        const imageUrl: string = hit.LibraryThumbs.Thumb_lg;
        const imageSizes = getImagesSizes({
            desktop: index % 2 === 0 ? 434 : 340,
            mobile: 204,
        });
        const link = !isDesktop
            ? getPreloadImageLink(undefined, imageUrl, imageSizes)
            : getPreloadImageLink(imageUrl, undefined, imageSizes);
        return acc.concat(link);
    }, [] as MetaDescriptor[]);
};

export const handle: APHandle = {
    category: 'blogpost',
    allowFilters: false,
    hideContactArchiPro: true,
    professionalRevampLayout: true,
};

/**
 * Toggle between the legacy paginated journal and the revamped editorial
 * Articles page. Reads the revamp flags from the shared layout loader so the
 * choice matches the route loader's (which serves the matching data shape).
 */
const ProfessionalJournalRoute = () => {
    const layoutData = useRouteLoaderData<typeof layoutLoader>(
        'routes/_app.professional.$urlSegment',
    );
    const [searchParams] = useSearchParams();
    const showRevamp =
        layoutData?.detailsRevamp ||
        (import.meta.env.DEV &&
            (searchParams.get('revamp') === '1' ||
                searchParams.get('detailsRevamp') === '1'));

    return showRevamp ? <ProfessionalArticlesPageRevamp /> : <ArticlesPage />;
};

export const shouldRevalidate = shouldRevalidateLeaf;
export default ProfessionalJournalRoute;
